'use client'

import * as React from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useShippingRatesList } from '@/lib/api/shippingRates'
import { ShippingRatesTable } from './components/ShippingRatesTable'
import { MainTableState } from '@/lib/utils/table-state-utils'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { tableStateToDrizzQuery } from './utils/table-state-to-drizz-query'
import { Card, CardContent } from '@/components/ui/card'

/**
 * Store settings page - Shipping Rates section
 * Displays shipping rates for a specific store
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(ShippingRatesPage, PERMISSIONS.STORES_VIEW);

function ShippingRatesPage() {
	const { t } = useTranslation(['shippings', 'common'])
	const { id, storeRoutes } = useStoreNavigate()

	// Table state managed by ShippingRatesTable
	const [tableState, setTableState] = React.useState<MainTableState>({
		pagination: { pageIndex: 0, pageSize: 20 },
		sorting: [{ id: 'shippingRateName', desc: false }],
		columnFilters: [],
	})

	const queryInput = React.useMemo(() => {
		return tableStateToDrizzQuery(tableState, id)
	}, [tableState, id])

	const {
		data: shippingRatesResponse,
		isLoading: loading,
		error,
		refetch,
	} = useShippingRatesList(queryInput)

	const shippingRates = shippingRatesResponse?.items || []
	const total = shippingRatesResponse?.total || 0

	return (
		<div className="flex flex-col space-y-4 sm:space-y-6">
			{/* Page header */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
				<div>
					<h1 className="text-xl font-semibold sm:text-2xl">{t('title')}</h1>
					<p className="text-muted-foreground text-sm">{t('description')}</p>
				</div>
				<div className="flex items-center gap-2">
					<Link to={storeRoutes.SHIPPING_CREATE()}>
						<Button className="w-full sm:w-auto">
							<Plus className="mr-2 h-4 w-4" />
							{t('addShippingRate')}
						</Button>
					</Link>
				</div>
			</div>

			{/* Shipping rates table */}
			<Card>
				<CardContent>
					<ShippingRatesTable
						shippingRates={shippingRates}
						total={total}
						loading={loading}
						onTableStateChange={setTableState}
						onRefetch={refetch}
					/>
				</CardContent>
			</Card>
			{/* Error message */}
			{error && (
				<div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
					{error instanceof Error ? error.message : t('loadError')}
				</div>
			)}
		</div>
	)
}